import { Controller } from "@hotwired/stimulus";

const PASSWORD_MIN_LENGTH = 8;
const SUBDOMAIN_DEBOUNCE_MS = 500;
const SUBDOMAIN_CHECK_MAX_WAIT_MS = 5000;
const SUBDOMAIN_CHECK_RETRY_MS = 200;
// Parsley's own default, plus :hidden so steps the visitor hasn't reached are skipped
const PARSLEY_EXCLUDED = "input[type=button], input[type=submit], input[type=reset], input[type=hidden], :hidden";
// The step swap has to finish before the browser can focus the offending field
const REPORT_VALIDITY_DELAY_MS = 500;

export default class extends Controller {
  static targets = [
    "form", "subdomainGroup", "subdomainField", "subdomainText", "subdomainHelp", "subdomainError",
    "step", "stepDot", "formError", "formErrorText"
  ];

  // multiStep false renders the single-step form: all steps visible, no step navigation
  static values = { multiStep: Boolean, availabilityUrl: String, checkFailedMessage: String };

  connect () {
    // Without the exclusion an implicit submit validates every step at once, which
    // paints "this value is required" on a step before the visitor even sees it.
    if (this.hasFormTarget && window.$) $(this.formTarget).parsley({ excluded: PARSLEY_EXCLUDED });

    if (this.multiStepValue) {
      // 0 means "nothing tracked yet" (steps are 1-based), so the trackStep call
      // below always fires for the step the visitor lands on
      this._furthestCompletedStep = 0;

      // jump to the step that owns the error when the server re-renders with errors
      const startStep = parseInt(this.formTarget?.dataset?.startStep || 1);
      if (startStep > 1) this.showStep(startStep);
      this.trackStep(startStep);

      if (this.formTarget?.dataset?.signupErrors === "true") {
        window.dataLayer?.push({ event: "trial_signup_error", trialStep: startStep });
      }
    }

    // ponytail: global hook for reCAPTCHA invisible callback
    window.trialPageSubmitForm = () => this.submitForm();

    this._lastCheck = null;
    this._lastCompanyValue = null;
    this._checkTimeout = null;
    this._subdomainCheckInProgress = false;
    this._submitFormRetryStart = null;
    this._passwordHelp = this.element.querySelector("#password-min-length-error");
    this._companyHelperText = this.element.querySelector("#subdomain-helper-text");

    this._checkInitialSubdomain();
  }

  disconnect () {
    delete window.trialPageSubmitForm;
    clearTimeout(this._checkTimeout);
  }

  // the control is a link for styling only, it reveals the field instead of navigating
  editSubdomain (event) {
    event.preventDefault();

    this.subdomainGroupTarget.classList.add("active");
    this.subdomainTextTarget.classList.remove("active");
    this.subdomainFieldTarget.focus();
  }

  onPasswordInput (event) {
    if (!this._passwordHelp) return;

    this._passwordHelp.style.color = event.target.value.length < PASSWORD_MIN_LENGTH ? "red" : "green";
  }

  // Suggest a subdomain based on the company name, for as long as the visitor has not
  // typed a subdomain of their own.
  onCompanyInput (event) {
    if (!this.hasSubdomainFieldTarget) return;

    let companyValue = event.target.value;
    const subdomainField = this.subdomainFieldTarget;

    if (subdomainField.value !== "" && subdomainField.value !== this._lastCompanyValue) {
      this._lastCompanyValue = companyValue;
      return;
    }

    // drop the punctuation of suffixes like "B.V." or "Ltd." so the suggestion stays readable
    if (companyValue.match(/\W\w{2,3}(\W|$)/) !== null) companyValue = companyValue.replace(/\W/g, "");

    subdomainField.value = companyValue;
    this._normalizeSubdomainField();
    this._scheduleSubdomainCheck();
    // normalizing changes the field synchronously, so remember the normalized value or the
    // comparison above never matches on the next keystroke
    this._lastCompanyValue = subdomainField.value;
  }

  onSubdomainInput () {
    this._normalizeSubdomainField();
    this._scheduleSubdomainCheck();
  }

  // Enter used to submit the whole form from any step, which made Parsley flag the
  // fields on steps the visitor hadn't filled in yet. Advance the form instead.
  onFormKeydown (event) {
    if (!this.multiStepValue || event.key !== "Enter") return;
    // buttons, links and the Selectize country dropdown handle Enter themselves
    if (event.target.closest("button, a, textarea, input[type='submit'], .selectize-control")) return;

    const currentStep = this.stepTargets.findIndex(step => !step.hidden) + 1;
    if (currentStep === 0) return;

    event.preventDefault();

    if (currentStep < this.stepTargets.length) {
      this._goToNextStep(currentStep);
    } else {
      // click rather than submit so the reCAPTCHA challenge still runs
      this.element.querySelector(".trial-step-submit button, .trial-step-submit input[type='submit']")?.click();
    }
  }

  nextStep (event) {
    this._goToNextStep(event.params.step);
  }

  backStep (event) {
    this.showStep(event.params.step - 1);
  }

  _goToNextStep (current) {
    const fields = this.stepTargets[current - 1].querySelectorAll("input:not([type='hidden']), select");
    const invalidField = Array.from(fields).find(field => field.willValidate && !field.checkValidity());

    if (invalidField) {
      this._reportField(invalidField);
      return;
    }

    this._hideFormError();

    if (current === 1) {
      this._leadCreation = this._createLead();
    } else {
      this._updateLeadStep(current);
    }
    this.showStep(current + 1);
    this.stepTargets[current]?.querySelector("input:not([type='hidden']), select")?.focus();
  }

  showStep (step) {
    this.trackStep(step);

    this.stepTargets.forEach((stepEl, i) => {
      stepEl.hidden = i + 1 !== step;
    });

    this.stepDotTargets.forEach((dot, i) => {
      dot.classList.toggle("active", i + 1 === step);
      dot.classList.toggle("completed", i + 1 < step);
    });
  }

  // GTM funnel event, fired once per step so we can see where visitors drop out
  trackStep (step) {
    if (step <= this._furthestCompletedStep) return;
    this._furthestCompletedStep = step;
    window.dataLayer?.push({ event: "trial_signup_step", trialStep: step });
  }

  // Without reCAPTCHA the button submits natively, which would skip the subdomain check handling below.
  submit (event) {
    event.preventDefault();
    this.submitForm();
  }

  submitForm () {
    if (this._subdomainCheckInProgress && this._waitingForSubdomainCheck()) {
      setTimeout(() => this.submitForm(), SUBDOMAIN_CHECK_RETRY_MS);
      return;
    }

    this._submitFormRetryStart = null;

    if (this.subdomainGroupTarget.classList.contains("error")) {
      this.subdomainFieldTarget.classList.add("error");
      this.subdomainFieldTarget.focus();
      this._showFormError(this.subdomainErrorTarget.textContent);
      this._resetRecaptcha();
      return;
    }

    if (this.formTarget.checkValidity()) {
      this._hideFormError();
      this.formTarget.submit();
      return;
    }

    this._resetRecaptcha();
    this._reportInvalidField(this.formTarget);
  }

  // Give an in-flight availability check a moment to land, but never hold up the signup
  // longer than that: the server validates the subdomain on create anyway.
  _waitingForSubdomainCheck () {
    this._submitFormRetryStart ??= Date.now();
    if (Date.now() - this._submitFormRetryStart < SUBDOMAIN_CHECK_MAX_WAIT_MS) return true;

    this._subdomainCheckInProgress = false;
    return false;
  }

  // After a failed signup the server re-renders the form without the password and
  // drops the visitor on the step that owns the error, so a required field can end
  // up blank on a hidden step. The browser refuses to submit but can't focus the
  // hidden field to say why — jump to the step that owns it before reporting, and
  // fall back to an inline notice for fields the browser can't point at (a select
  // replaced by Selectize, for instance) so the submit is never refused silently.
  _reportInvalidField (form) {
    const invalidField = Array.from(form.elements).find(element => element.willValidate && !element.checkValidity());
    if (!invalidField) return;

    if (this.multiStepValue) {
      const invalidStep = this.stepTargets.findIndex(step => step.contains(invalidField));
      if (invalidStep !== -1) this.showStep(invalidStep + 1);
    }

    setTimeout(() => this._reportField(invalidField), REPORT_VALIDITY_DELAY_MS);
  }

  // A field the browser can't point at — hidden on another step, or a select
  // replaced by Selectize — shows no native bubble, so fall back to a notice.
  _reportField (field) {
    if (field.offsetParent === null) {
      this._showFormError(field.validationMessage);
    } else {
      this._hideFormError();
      field.reportValidity();
    }
  }

  _showFormError (message) {
    if (!this.hasFormErrorTarget || !message) return;

    this.formErrorTextTarget.textContent = message;
    this.formErrorTarget.hidden = false;
    this.formErrorTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  _hideFormError () {
    if (this.hasFormErrorTarget) this.formErrorTarget.hidden = true;
  }

  // Only reset when we stay on the page so the next click re-executes the challenge.
  // Resetting right after the reCAPTCHA callback wipes the token before a deferred
  // submit (waiting on the subdomain check) and the server then rejects the signup.
  _resetRecaptcha () {
    window.grecaptcha?.reset();
  }

  // check right away when the form re-renders with a subdomain, e.g. after a server-side
  // validation error, so the domain line shows again without the visitor typing
  _checkInitialSubdomain () {
    if (!this.hasSubdomainFieldTarget) return;

    if (this.subdomainFieldTarget.value === "") {
      this._lastCheck = "";
    } else {
      this._checkSubdomain();
    }
  }

  _normalizeSubdomainField () {
    this.subdomainFieldTarget.value = this.subdomainFieldTarget.value.replace(/\s+/g, "-").toLowerCase();
  }

  _scheduleSubdomainCheck () {
    clearTimeout(this._checkTimeout);
    this._checkTimeout = setTimeout(() => this._checkSubdomain(), SUBDOMAIN_DEBOUNCE_MS);
  }

  async _createLead () {
    const email = this.element.querySelector("#user_email")?.value;
    if (!email) return;

    // visitors landing on the form directly have no locale param, so fall back to
    // the page language — the lead's language drives the AI webinar suggestions
    const locale = this.element.querySelector("input[name='locale']")?.value ||
      this.element.querySelector("input[name='user[language]']")?.value || "";
    // step 1 is complete once the visitor submits their email
    const body = new URLSearchParams({ "account_lead[email]": email, locale, step: 1 });

    try {
      const response = await fetch("/account/create_lead.json", { method: "POST", body });
      const data = await response.json();

      if (data.direct_link) {
        const activationCode = new URL(data.direct_link).searchParams.get("activation_code");
        if (activationCode) {
          const hidden = this.element.querySelector("input[name='activation_code']");
          if (hidden) hidden.value = activationCode;

          const url = new URL(window.location.href);
          url.searchParams.set("activation_code", activationCode);
          history.replaceState(null, "", url);
        }
      }
    } catch (_) {
      // fire and forget — lead creation is best-effort, don't block the signup flow
    }
  }

  // persists the furthest completed step on the lead, mirroring the GTM funnel events
  async _updateLeadStep (step) {
    // the lead may still be being created if the visitor speeds through step 2
    await this._leadCreation;

    const activationCode = this.element.querySelector("input[name='activation_code']")?.value;
    if (!activationCode) return;

    try {
      await fetch("/account/update_lead_step", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": this._csrfToken() },
        body: new URLSearchParams({ activation_code: activationCode, step })
      });
    } catch (_) {
      // fire and forget — step tracking must never block the signup flow
    }
  }

  async _checkSubdomain () {
    const subdomain = this.subdomainFieldTarget.value;
    if (this._lastCheck === subdomain) return;

    this._lastCheck = subdomain;
    this.subdomainFieldTarget.classList.remove("error");

    if (!subdomain) {
      this.subdomainFieldTarget.classList.add("error");
      this._subdomainCheckInProgress = false;
      return;
    }

    this._subdomainCheckInProgress = true;

    try {
      const data = await this._requestSubdomainAvailability(subdomain);

      if (data.state === "error") {
        this._showSubdomainTaken(data.message);
      } else {
        this._showSubdomainAvailable(data.message, data.subdomain);
      }
    } catch (error) {
      console.error(error);
      this._showSubdomainCheckFailed();
    } finally {
      this._subdomainCheckInProgress = false;
    }
  }

  async _requestSubdomainAvailability (subdomain) {
    const response = await fetch(this.availabilityUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": this._csrfToken() },
      body: new URLSearchParams({ subdomain })
    });

    if (!response.ok) throw new Error(`Subdomain availability check failed with status ${response.status}`);

    return response.json();
  }

  _showSubdomainAvailable (message, subdomain) {
    this.subdomainHelpTarget.classList.remove("error");
    this.subdomainGroupTarget.classList.remove("error");
    this.subdomainHelpTarget.classList.add("success");
    this.subdomainGroupTarget.classList.add("success");

    this._hideCompanyHelperText();

    this.subdomainHelpTarget.textContent = message;
    this.subdomainFieldTarget.value = subdomain;
    this.subdomainTextTarget.classList.add("active");
    this.subdomainErrorTarget.style.display = "none";
    this.subdomainHelpTarget.classList.add("active");
  }

  _showSubdomainTaken (message) {
    this.subdomainHelpTarget.classList.remove("success");
    this.subdomainGroupTarget.classList.remove("success");
    this.subdomainHelpTarget.classList.add("error");
    this.subdomainGroupTarget.classList.add("error");

    // reveal the input so the visitor can correct the subdomain
    this.subdomainGroupTarget.classList.add("active");

    this._hideCompanyHelperText();

    this.subdomainTextTarget.classList.remove("active");
    this.subdomainErrorTarget.style.display = "";
    this.subdomainErrorTarget.textContent = message;
  }

  // A failing request says nothing about the subdomain itself, so we tell the visitor what
  // happened without marking the field invalid: that state blocks submitting, while the
  // server still validates the subdomain on create. Clearing the last checked value lets
  // the same subdomain be checked again on the next keystroke.
  _showSubdomainCheckFailed () {
    this._lastCheck = null;

    this.subdomainHelpTarget.classList.remove("success", "error");
    this.subdomainGroupTarget.classList.remove("success", "error");
    this.subdomainGroupTarget.classList.add("active");

    this.subdomainTextTarget.classList.remove("active");
    this.subdomainErrorTarget.style.display = "";
    this.subdomainErrorTarget.textContent = this.checkFailedMessageValue;
  }

  _hideCompanyHelperText () {
    if (this._companyHelperText) this._companyHelperText.style.display = "none";
  }

  _csrfToken () {
    return document.querySelector("meta[name='csrf-token']")?.content;
  }
}
